import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AttachmentsDTO,
  ErrorDisplayService,
  FileUploadService,
} from '@esp-enrollment-form/shared-utils';
import { Subscription } from 'rxjs';

import { CompassButtonModule } from '@pwc/compass/button';
import { CompassCalloutModule } from '@pwc/compass/callout';
import { CompassIconModule, CompassIconService } from '@pwc/compass/icon';

import {
  AllowedExtensionsPipe,
  WrongExtensionsMessagePipe,
} from './extentions.pipe';
import { FileForUpload, FileUploadOutput } from './file-upload.model';

@Component({
  selector: 'esp-fileupload',
  imports: [
    CommonModule,
    CompassCalloutModule,
    MatCheckboxModule,
    CompassButtonModule,
    AllowedExtensionsPipe,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    WrongExtensionsMessagePipe,
    CompassIconModule,
    MatTooltipModule,
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent implements OnDestroy, OnInit {

  fileUpload = viewChild<ElementRef>('fileUpload');
  allowedExtensions = input<string[]>([]);
  isTechnicalConfirmationVisible = input(true);
  isMultiuploadEnabled = input(true);
  maxFilesNumber = input(5);
  // Max file's size in MB
  maxFileSize = input(5);
  referenceNumber = input();
  fileChanged = output<FileUploadOutput>();
  deleteDraftFile = output<AttachmentsDTO>();
  deleteUploadedFile = output<string>();
  draftFiles = input<AttachmentsDTO[] | null>(null);
  removePendingFiles = input<boolean>();

  draftFilesForDisplay = signal<AttachmentsDTO[]>([]);
  uploadedFilesSignal = signal<File[]>([]);
  fileUploadControl = new FormControl();
  checkboxControl = new FormControl(false);

  tooManyFiles = signal(false);
  wrongExtension = signal(false);
  tooLargeFile = signal(false);
  wrongExtensionsList = signal<(string | undefined)[]>([]);
  tooLargeFilesNames = signal<string[]>([]);
  uploadedFilesVisible = signal<boolean>(true);

  isErrorVisible = computed<boolean>(
    () => this.tooManyFiles() || this.wrongExtension() || this.tooLargeFile(),
  );
  filesNumber = computed<number>(() => {
    const draftFiles = this.draftFilesForDisplay();
    if (draftFiles) {
      return this.uploadedFilesSignal().length + draftFiles.length;
    } else {
      return this.uploadedFilesSignal().length;
    }
  });

  private _iconService: CompassIconService = inject(CompassIconService);
  private _errorDisplay = inject(ErrorDisplayService);

  private _fileUploadService = inject(FileUploadService);
  private _subscription = new Subscription();

  constructor() {
    this._iconService.registerAppIcon('delete');

    effect(() => {
      const draftFiles = this.draftFiles() ?? [];
      this.draftFilesForDisplay.set(draftFiles);
      this.checkboxControl.setValue(draftFiles.length > 0);
      this.checkboxControl.updateValueAndValidity();
    });

    effect(() => {
      if (this.isTechnicalConfirmationVisible()) {
        this.checkboxControl.addValidators(Validators.requiredTrue);
        this.checkboxControl.updateValueAndValidity();
      }
    });

    effect(() => {
      if (this.removePendingFiles()) {
        this.uploadedFilesVisible.set(false);
        this.uploadedFilesSignal.update(() => []);
      }
    });
  }

  ngOnInit(): void {
    this._subscription.add(
      this._fileUploadService.onRemoveFile$.subscribe((nameForUpload) => {
        this.uploadedFilesSignal.update((files) =>
          files.filter((f) => (f as FileForUpload).nameForUpload !== nameForUpload)
        );
      })
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  removeFileFromSignal(nameForUpload: string) {
    this.uploadedFilesSignal.update((files) =>
      files.filter((f) => (f as FileForUpload).nameForUpload !== nameForUpload)
    );
  }

  onFileSelected() {
    const formFiles: File[] = Array.from(
      this.fileUpload()?.nativeElement.files || [],
    );

    const addedFiles: FileForUpload[] = [];
    const skippedFiles: FileForUpload[] = [];

    formFiles.forEach((file: FileForUpload) => {
      const { fileName, fileExtension } = this.getFilenameAndExtension(
        file.name,
      );
      file.nameForUpload = `${fileName}-${this.referenceNumber()}.${fileExtension}`;
      const alreadyExist = this.uploadedFilesSignal().some(
        (existing) =>
          existing.name === file.name && existing.type === file.type,
      );

      if (alreadyExist) {
        skippedFiles.push(file);
      } else {
        addedFiles.push(file);
      }
    });

    if (addedFiles?.length > 0) {
      this.uploadedFilesSignal.update(() => [
        ...this.uploadedFilesSignal(),
        ...addedFiles,
      ]);
      this.uploadedFilesVisible.set(true);
      this.validateFileSize(addedFiles);
      this.validateFileExtension(addedFiles);
    }

    this.validateNumberOfFiles();
    this.checkIfErrorsCanBeRemoved();
    this.showError();
    this.emitFiles();

    if (skippedFiles.length > 0) {
      const rejectedFiles =
        skippedFiles.length === 1
          ? skippedFiles[0].name
          : skippedFiles.map((file) => file.name).join(', ');
      this._errorDisplay.showError(
        `You can't add files with the same name and extension: ${rejectedFiles}`,
      );
    }
  }

  removeUploadedFile(removedIndex: number, filename: string) {
    const { fileName, fileExtension } = this.getFilenameAndExtension(filename);

    const fileNnameForDelete = `${fileName}-${this.referenceNumber()}.${fileExtension}`;

    this.uploadedFilesSignal.update(() =>
      this.uploadedFilesSignal().filter(
        (_file, index) => index !== removedIndex,
      ),
    );

    this.checkIfErrorsCanBeRemoved();
    this.tooLargeFile.set(false);
    this.wrongExtension.set(false);
    this.emitFiles();
    this.deleteUploadedFile.emit(fileNnameForDelete);
  }

  removeDraftFile(file: AttachmentsDTO) {
    const draftFiles = this.draftFilesForDisplay().filter(
      (f) => f.id !== file.id,
    );
    this.draftFilesForDisplay.set(draftFiles);

    this.checkIfErrorsCanBeRemoved();
    this.tooLargeFile.set(false);
    this.wrongExtension.set(false);
    this.deleteDraftFile.emit(file);
  }

  emitFiles() {
    this.fileChanged.emit({
      files: this.uploadedFilesSignal(),
      isValid:
        !this.tooManyFiles() &&
        this.checkboxControl.valid &&
        this.filesNumber() > 0,
    });
  }

  private showError() {
    this.fileUploadControl.markAsTouched();
  }

  private checkIfErrorsCanBeRemoved() {
    if (this.filesNumber() <= this.maxFilesNumber()) {
      this.tooManyFiles.set(false);
    }
  }

  private validateNumberOfFiles() {
    if (this.filesNumber() > this.maxFilesNumber()) {
      this.tooManyFiles.set(true);
    } else {
      this.tooManyFiles.set(false);
    }
  }

  private validateFileSize(files: File[]) {
    const maxSize = this.maxFileSize() * 1024 * 1024;
    const tooBigFiles = Array.from(files).filter(
      (file) => file.size >= maxSize,
    );
    const fileNames = tooBigFiles.map((file) => file.name);

    if (fileNames.length > 0) {
      this.tooLargeFilesNames.set(fileNames);
      this.tooLargeFile.set(true);

      this.uploadedFilesSignal.update(() =>
        this.uploadedFilesSignal().filter(
          (file) => !fileNames.includes(file.name),
        ),
      );
    } else {
      this.tooLargeFile.set(false);
    }
  }

  private validateFileExtension(files: File[]) {
    const wrongExtensions = Array.from(files)
      .map((file) => this.getFileExtension(file.name))
      .filter((file) => this.isNotAllowedExtension(file || ''));

    if (wrongExtensions.length > 0) {
      const wrongExtensionsUnique = new Set(wrongExtensions);
      this.wrongExtensionsList.set([...wrongExtensionsUnique]);
      this.wrongExtension.set(true);

      this.uploadedFilesSignal.update(() =>
        this.uploadedFilesSignal().filter(
          (file) => !wrongExtensions.includes(this.getFileExtension(file.name)),
        ),
      );
    } else {
      this.wrongExtension.set(false);
    }
  }

  private getFileExtension(fileName: string) {
    return fileName?.split('.')?.pop()?.toLowerCase();
  }

  private isNotAllowedExtension(extension: string) {
    return extension ? !this.allowedExtensions().includes(extension) : false;
  }

  private getFilenameAndExtension(fullFilename: string): {
    fileName: string;
    fileExtension: string;
  } {
    const lastDotIndex = fullFilename.lastIndexOf('.');

    if (
      lastDotIndex === -1 ||
      lastDotIndex === 0 ||
      lastDotIndex === fullFilename.length - 1
    ) {
      return { fileName: fullFilename, fileExtension: '' };
    }

    const fileName = fullFilename.substring(0, lastDotIndex);
    const fileExtension = fullFilename.substring(lastDotIndex + 1);

    return { fileName, fileExtension };
  }
}
